import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Camera, CheckCircle, FilePdf, Warning, WarningCircle, Clock, X, ChatDots, FileArrowUp, Sparkle, EnvelopeSimple, PaperPlaneRight, User, Link, Copy } from '@phosphor-icons/react';
import { SignatureModal } from './SignatureModal';
import { fmtDateFR, fmtDateTimeFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 2;

const STATUS_META = {
  en_cours: { label: 'En cours', bg: '#FFF3E0', color: '#EF6C00', icon: Clock },
  fait: { label: 'Fait', bg: '#E8F5E9', color: '#2E7D32', icon: CheckCircle },
  pas_fait: { label: 'Pas fait', bg: '#FFEBEE', color: '#C62828', icon: WarningCircle },
};

const StatusBadge = ({ status, onClick }) => {
  const meta = STATUS_META[status] || STATUS_META.en_cours;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`reserv-status-${status}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', backgroundColor: meta.bg, color: meta.color, border: 'none', cursor: onClick ? 'pointer' : 'default' }}
    >
      <Icon size={12} weight="bold" /> {meta.label}
    </button>
  );
};


// ============================================================================
// AddReservationModal — opens on "+" click; multi-photos + datalist autocomplete
// ============================================================================
const AddReservationModal = ({ project, onClose, onCreated, subcontractors, employees, knownLocations, isAdmin }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [openedAt, setOpenedAt] = useState('');
  const [assigneeType, setAssigneeType] = useState('');  // '' | 'subcontractor' | 'employee'
  const [assigneeId, setAssigneeId] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);  // File[]
  const [busy, setBusy] = useState(false);

  const onSelectFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length + photoFiles.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }
    setPhotoFiles((prev) => [...prev, ...files].slice(0, MAX_PHOTOS));
  };

  const removeFileAt = (idx) => setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error('Titre requis');
    setBusy(true);
    try {
      // 1) Create the reservation
      const body = {
        title,
        description,
        location,
        opened_at: openedAt || null,
      };
      if (isAdmin && assigneeId && assigneeType) {
        body.assigned_to_id = assigneeId;
        body.assigned_to_type = assigneeType;
      }
      const created = await axios.post(`${API_URL}/api/reservations/project/${project.id}`, body, { withCredentials: true });
      const newId = created.data?.id;
      // 2) Upload photos (multi-file batch)
      if (newId && photoFiles.length > 0) {
        const fd = new FormData();
        photoFiles.forEach((f) => fd.append('files', f));
        await axios.post(`${API_URL}/api/reservations/${newId}/photos/batch`, fd, { withCredentials: true });
      }
      toast.success('Reserve creee');
      onCreated && onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10004, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px' }} data-testid="reserv-add-modal">
      <form onSubmit={submit} style={{ background: 'white', borderRadius: '12px', maxWidth: '560px', width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Nouvelle reserve</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'grid', gap: '12px' }}>
          <div>
            <label className="form-label" style={{ fontSize: '0.82rem' }}>Titre *</label>
            <input type="text" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="reserv-new-title" placeholder="Ex : Porte bloquee" autoFocus />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '0.82rem' }}>Localisation</label>
            <input
              type="text"
              className="form-input"
              list="reserv-locations-list"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="reserv-new-location"
              placeholder="Piece, etage... (autocomplete des saisies precedentes)"
            />
            <datalist id="reserv-locations-list">
              {knownLocations.map((loc) => <option key={loc} value={loc} />)}
            </datalist>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '0.82rem' }}>Description</label>
            <textarea className="form-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="reserv-new-desc" placeholder="Detail..." />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '0.82rem' }}>Date d'ouverture (vide = aujourd'hui)</label>
            <input type="date" className="form-input" value={openedAt} onChange={(e) => setOpenedAt(e.target.value)} />
          </div>

          {isAdmin && (
            <div style={{ background: '#F8F9FB', padding: '10px 12px', borderRadius: '8px' }}>
              <label className="form-label" style={{ fontSize: '0.82rem', display: 'block', marginBottom: '6px' }}>Affecter a (optionnel)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px' }}>
                <select
                  className="form-input"
                  value={assigneeType}
                  onChange={(e) => { setAssigneeType(e.target.value); setAssigneeId(''); }}
                  data-testid="reserv-new-assignee-type"
                >
                  <option value="">-- Personne --</option>
                  <option value="subcontractor">Sous-traitant</option>
                  <option value="employee">Salarie</option>
                </select>
                <select
                  className="form-input"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  disabled={!assigneeType}
                  data-testid="reserv-new-assignee-id"
                >
                  <option value="">-- Selectionner --</option>
                  {assigneeType === 'subcontractor' && subcontractors.map((s) => (
                    <option key={s.id} value={s.id}>{s.company_name || s.name}</option>
                  ))}
                  {assigneeType === 'employee' && employees.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` - ${u.job_title}` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="form-label" style={{ fontSize: '0.82rem' }}>Photos (max {MAX_PHOTOS})</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={onSelectFiles}
              data-testid="reserv-new-photos"
              disabled={photoFiles.length >= MAX_PHOTOS}
              style={{ marginTop: 4 }}
            />
            {photoFiles.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                {photoFiles.map((f, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <img src={URL.createObjectURL(f)} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #E2E2E6' }} />
                    <button
                      type="button"
                      onClick={() => removeFileAt(idx)}
                      style={{ position: 'absolute', top: -6, right: -6, background: '#C62828', color: 'white', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
                      aria-label="Retirer la photo"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="reserv-new-submit">
            <Plus size={14} weight="bold" /> {busy ? 'Creation...' : 'Creer la reserve'}
          </button>
        </div>
      </form>
    </div>
  );
};

const ReportIssueDialog = ({ reservation, onClose, onReport }) => {
  const [note, setNote] = useState('');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px' }} data-testid="report-issue-modal">
      <div style={{ background: 'white', borderRadius: '10px', maxWidth: '420px', width: '100%' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Signaler un probleme</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E5F66' }}><X size={20} /></button>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginBottom: '8px' }}>Reserve : <b>{reservation.title}</b></div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="form-input" placeholder="Decrivez le probleme rencontre..." data-testid="report-issue-note" style={{ width: '100%', resize: 'vertical' }} />
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={() => note.trim() && onReport(note)} disabled={!note.trim()} data-testid="report-issue-submit">Signaler</button>
        </div>
      </div>
    </div>
  );
};

const ReservationRow = ({ r, isAdmin, onStatusChange, onDelete, onEdit, onUploadPhoto, onSign, onReport, onViewIssues, onAssignST }) => {
  const meta = STATUS_META[r.status] || STATUS_META.en_cours;
  const fileRef = useRef(null);
  const isSigned = Boolean(r.client_signature_data_url);
  return (
    <tr data-testid={`reserv-row-${r.id}`} style={{ borderLeft: `3px solid ${meta.color}` }}>
      <td style={{ maxWidth: '340px' }}>
        <div style={{ fontWeight: '700', color: '#1E2030' }}>{r.title}</div>
        {r.description && <div style={{ fontSize: '0.8rem', color: '#5E5F66', marginTop: '2px' }}>{r.description}</div>}
        {r.location && <div style={{ fontSize: '0.75rem', color: '#9E9EA7', marginTop: '2px' }}>Lieu : {r.location}</div>}
        <div style={{ fontSize: '0.72rem', color: '#9E9EA7', marginTop: '3px' }}>
          Cree par {r.created_by_name} ({r.created_by_role === 'admin' ? 'admin' : 'salarie'})
        </div>
      </td>
      <td>
        {isAdmin ? (
          <select
            value={r.status}
            onChange={(e) => onStatusChange(r.id, e.target.value)}
            data-testid={`reserv-select-status-${r.id}`}
            style={{ backgroundColor: meta.bg, color: meta.color, fontWeight: '700', fontSize: '0.78rem', padding: '4px 8px', border: `1px solid ${meta.color}33`, borderRadius: '6px', cursor: 'pointer' }}
            title={isSigned ? 'Reserve signee - modif reservee admin' : ''}
          >
            <option value="en_cours">En cours</option>
            <option value="fait">Fait</option>
            <option value="pas_fait">Pas fait</option>
          </select>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: isSigned ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: '600', color: r.status === 'fait' ? '#2E7D32' : '#5E5F66', opacity: isSigned ? 0.7 : 1 }}>
              <input
                type="checkbox"
                checked={r.status === 'fait'}
                disabled={isSigned}
                onChange={(e) => onStatusChange(r.id, e.target.checked ? 'fait' : 'en_cours')}
                data-testid={`reserv-cb-fait-${r.id}`}
                style={{ width: '18px', height: '18px', accentColor: '#2E7D32', cursor: isSigned ? 'not-allowed' : 'pointer' }}
                title={isSigned ? 'Scellee par signature' : ''}
              />
              Levee{isSigned ? ' (scellee)' : ''}
            </label>
            <StatusBadge status={r.status} />
          </div>
        )}
      </td>
      <td style={{ fontSize: '0.82rem' }}>{fmtDateFR(r.opened_at) || '-'}</td>
      <td style={{ fontSize: '0.82rem' }}>{fmtDateFR(r.resolved_at) || '-'}</td>
      <td>
        {(r.photos && r.photos.length > 0) ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 120 }}>
            {r.photos.map((p, idx) => (
              <a key={idx} href={`${API_URL}${p.url}?t=${r.id}-${idx}`} target="_blank" rel="noreferrer">
                <img src={`${API_URL}${p.url}?t=${r.id}-${idx}`} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} />
              </a>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: '0.72rem', color: '#9E9EA7' }}>-</span>
        )}
      </td>
      <td style={{ fontSize: '0.78rem' }}>
        {r.assigned_to_name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 12, fontWeight: 600,
              background: r.assigned_to_type === 'subcontractor' ? '#EFF6FF' : '#F0FDF4',
              color: r.assigned_to_type === 'subcontractor' ? '#1E40AF' : '#15803D',
            }}>
              <User size={12} /> {r.assigned_to_name}
              <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                ({r.assigned_to_type === 'subcontractor' ? 'ST' : 'Salarie'})
              </span>
            </span>
          </div>
        ) : (
          <span style={{ color: '#9E9EA7', fontStyle: 'italic' }}>Non affecte</span>
        )}
      </td>
      <td>
        {isSigned ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <img src={r.client_signature_data_url} alt="signature" style={{ maxWidth: '80px', maxHeight: '34px', border: '1px solid #E2E2E6', borderRadius: '3px' }} />
            <div style={{ fontSize: '0.7rem', color: '#5E5F66' }}>{r.client_name}</div>
          </div>
        ) : (
          <span style={{ fontSize: '0.72rem', color: '#9E9EA7' }}>Non signe</span>
        )}
      </td>
      <td>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadPhoto(r.id, f); e.target.value=''; }} style={{ display: 'none' }} />
          <button type="button" onClick={() => fileRef.current?.click()} title="Uploader/changer photo" className="btn btn-secondary btn-sm" data-testid={`reserv-photo-btn-${r.id}`} style={{ padding: '4px 6px' }}>
            <Camera size={14} />
          </button>
          {r.reported_issues && r.reported_issues.length > 0 && (
            <button type="button" onClick={() => onViewIssues(r)} title={`${r.reported_issues.length} signalement(s)`} className="btn btn-secondary btn-sm" data-testid={`reserv-issues-btn-${r.id}`} style={{ padding: '4px 6px', backgroundColor: '#FFF3E0', borderColor: '#EF6C00', color: '#EF6C00' }}>
              <ChatDots size={14} /> {r.reported_issues.length}
            </button>
          )}
          {isAdmin ? (
            <>
              <button type="button" onClick={() => onEdit(r)} title="Editer" className="btn btn-secondary btn-sm" data-testid={`reserv-edit-${r.id}`} style={{ padding: '4px 6px' }}>
                <PencilSimple size={14} />
              </button>
              <button type="button" onClick={() => onAssignST(r)} title={r.subcontractor_name ? `Affecté à ${r.subcontractor_name} - Courrier` : 'Affecter à un ST + courrier'} className="btn btn-sm" data-testid={`reserv-st-${r.id}`} style={{ padding: '4px 6px', background: r.subcontractor_name ? '#FEF3C7' : '#EFF6FF', borderColor: r.subcontractor_name ? '#F59E0B' : '#002FA7', color: r.subcontractor_name ? '#92400E' : '#002FA7' }}>
                <EnvelopeSimple size={14} />
              </button>
              <button type="button" onClick={() => onSign(r)} title={isSigned ? 'Remplacer signature' : 'Faire signer'} className={`btn btn-sm ${isSigned ? 'btn-secondary' : 'btn-primary'}`} data-testid={`reserv-sign-${r.id}`} style={{ padding: '4px 6px' }}>
                <FileArrowUp size={14} />
              </button>
              <button type="button" onClick={() => onDelete(r.id, r.title)} title="Supprimer" className="btn btn-danger btn-sm" data-testid={`reserv-del-${r.id}`} style={{ padding: '4px 6px' }}>
                <Trash size={14} />
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => onSign(r)} title={isSigned ? 'Signee' : 'Faire signer le client'} disabled={isSigned} className={`btn btn-sm ${isSigned ? 'btn-secondary' : 'btn-primary'}`} data-testid={`reserv-sign-${r.id}`} style={{ padding: '4px 6px', opacity: isSigned ? 0.5 : 1 }}>
                <FileArrowUp size={14} />
              </button>
              <button type="button" onClick={() => onReport(r)} title="Signaler un probleme" className="btn btn-secondary btn-sm" data-testid={`reserv-report-${r.id}`} style={{ padding: '4px 6px', color: '#EF6C00' }}>
                <Warning size={14} />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const EditDialog = ({ reservation, onClose, onSave }) => {
  const [form, setForm] = useState({ ...reservation });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px' }}>
      <div style={{ background: 'white', borderRadius: '10px', maxWidth: '460px', width: '100%' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Editer la reserve</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ padding: '14px 18px', display: 'grid', gap: '10px' }}>
          <div><label className="form-label" style={{ fontSize: '0.82rem' }}>Titre</label><input type="text" className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className="form-label" style={{ fontSize: '0.82rem' }}>Description</label><textarea className="form-input" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><label className="form-label" style={{ fontSize: '0.82rem' }}>Lieu</label><input type="text" className="form-input" value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><label className="form-label" style={{ fontSize: '0.82rem' }}>Date ouverture</label><input type="date" className="form-input" value={form.opened_at || ''} onChange={(e) => setForm({ ...form, opened_at: e.target.value })} /></div>
            <div><label className="form-label" style={{ fontSize: '0.82rem' }}>Date levee</label><input type="date" className="form-input" value={form.resolved_at || ''} onChange={(e) => setForm({ ...form, resolved_at: e.target.value })} /></div>
          </div>
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={() => onSave(form)}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const IssuesDialog = ({ reservation, onClose }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px' }}>
    <div style={{ background: 'white', borderRadius: '10px', maxWidth: '500px', width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Signalements : {reservation.title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px' }}>
        {(reservation.reported_issues || []).map(i => (
          <div key={i.id} style={{ borderLeft: '3px solid #EF6C00', padding: '8px 12px', marginBottom: '8px', background: '#FFF8E1', borderRadius: '0 6px 6px 0' }}>
            <div style={{ fontSize: '0.82rem', color: '#1E2030' }}>{i.note}</div>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: '4px' }}>{i.author_name} - {fmtDateTimeFR(i.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ReservationsTab = ({ project, currentUser }) => {
  const [data, setData] = useState({ items: [], total: 0, done: 0, signed: 0, all_signed: false, progress_pct: 0, pv_sent_at: null, pv_sent_to: '', client_email: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [signTarget, setSignTarget] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [issuesTarget, setIssuesTarget] = useState(null);
  const [stTarget, setStTarget] = useState(null);
  const [filter, setFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');  // 'all' | 'none' | <assignee_id>
  const [showAddModal, setShowAddModal] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [knownLocations, setKnownLocations] = useState([]);
  const [clientLinkLoading, setClientLinkLoading] = useState(false);
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrSelection, setOcrSelection] = useState({});
  const ocrFileRef = useRef(null);

  const isAdmin = currentUser?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reservations/project/${project.id}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  const fetchLocations = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/reservations/project/${project.id}/locations`, { withCredentials: true });
      setKnownLocations(r.data?.locations || []);
    } catch { /* silent */ }
  }, [project.id]);

  const fetchAssignableLists = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [sc, emp] = await Promise.all([
        axios.get(`${API_URL}/api/subcontractors`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users/employees`, { withCredentials: true }),
      ]);
      setSubcontractors(sc.data || []);
      setEmployees(emp.data || []);
    } catch { /* silent */ }
  }, [isAdmin]);

  useEffect(() => { fetchData(); fetchLocations(); fetchAssignableLists(); }, [fetchData, fetchLocations, fetchAssignableLists]);

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/reservations/${id}`, { status }, { withCredentials: true });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer la reserve "${title}" ?`)) return;
    try { await axios.delete(`${API_URL}/api/reservations/${id}`, { withCredentials: true }); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleUploadPhoto = async (id, file) => {
    if (file.size > 15 * 1024 * 1024) return toast.error('Photo trop lourde (max 15 Mo)');
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/reservations/${id}/photo`, fd, { withCredentials: true });
      toast.success('Photo ajoutee');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur upload'); }
  };
  const handleSign = async (payload) => {
    if (!signTarget) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/reservations/${signTarget.id}/sign`, payload, { withCredentials: true });
      toast.success('Signature enregistree - reserve levee');
      setSignTarget(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur signature'); }
    finally { setBusy(false); }
  };
  const handleReport = async (note) => {
    if (!reportTarget) return;
    try {
      await axios.post(`${API_URL}/api/reservations/${reportTarget.id}/report`, { note }, { withCredentials: true });
      toast.success('Signalement envoye');
      setReportTarget(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleEdit = async (form) => {
    try {
      const { id, title, description, location, opened_at, resolved_at } = form;
      await axios.put(`${API_URL}/api/reservations/${id}`, { title, description, location, opened_at: opened_at || null, resolved_at: resolved_at || null }, { withCredentials: true });
      setEditTarget(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pdf-reports/projects/${project.id}/reservations-pv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PV_Levee_Reserves_${(project.name || 'chantier').replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur export PDF'); }
  };

  const handleOcrFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error('Fichier trop lourd (max 15 Mo)');
    setOcrLoading(true);
    setOcrFile(file);
    setOcrResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/invoice-analysis/extract-reservations`, fd, { withCredentials: true });
      const list = res.data?.reservations || [];
      setOcrResult({ success: res.data?.success, reservations: list, error: res.data?.error });
      // Pre-select all extracted lines
      const sel = {};
      list.forEach((_, i) => { sel[i] = true; });
      setOcrSelection(sel);
      if (list.length === 0) toast.warning('Aucune reserve detectee');
      else toast.success(`${list.length} reserve(s) detectee(s) - verifiez et validez`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur analyse IA');
    } finally {
      setOcrLoading(false);
    }
  };

  const confirmOcrImport = async () => {
    if (!ocrResult?.reservations) return;
    const toImport = ocrResult.reservations.filter((_, i) => ocrSelection[i]);
    if (toImport.length === 0) { setOcrResult(null); return; }
    setBusy(true);
    try {
      for (const r of toImport) {
        await axios.post(`${API_URL}/api/reservations/project/${project.id}`, r, { withCredentials: true });
      }
      toast.success(`${toImport.length} reserve(s) importee(s)`);
      setOcrResult(null);
      setOcrFile(null);
      if (ocrFileRef.current) ocrFileRef.current.value = '';
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur import'); }
    finally { setBusy(false); }
  };

  const handleSendPV = async () => {
    if (!data.client_email && !window.confirm('Aucune adresse email client trouvee sur le chantier. L\'envoi sera ignore jusqu\'a ce que vous ajoutiez une adresse. Continuer ?')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/reservations/project/${project.id}/send-pv`, {}, { withCredentials: true });
      if (res.data?.sent) {
        toast.success(`PV envoye a ${res.data.sent_to}`);
      } else {
        toast.warning(data.client_email ? 'Envoi echoue - verifiez la config email' : 'Email client manquant - ajoutez-le sur le chantier');
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi PV');
    } finally { setBusy(false); }
  };

  const handleClientLink = async () => {
    setClientLinkLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/reservations/project/${project.id}/client-token`, {}, { withCredentials: true });
      try { await navigator.clipboard.writeText(r.data.url); } catch { /* clipboard may be blocked */ }
      toast.success('Lien client genere et copie dans le presse-papier', { duration: 6000, description: r.data.url });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setClientLinkLoading(false); }
  };

  const filteredItems = data.items
    .filter((i) => filter === 'all' || i.status === filter)
    .filter((i) => {
      if (assigneeFilter === 'all') return true;
      if (assigneeFilter === 'none') return !i.assigned_to_id;
      return i.assigned_to_id === assigneeFilter;
    });

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="reservations-tab">
      {/* Header progress */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Reserves du chantier</h3>
            <div style={{ fontSize: '0.82rem', color: '#5E5F66' }}>{data.done} / {data.total} levees - {data.progress_pct}%</div>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <input ref={ocrFileRef} type="file" accept="image/*,application/pdf" onChange={(e) => { handleOcrFile(e.target.files?.[0]); }} style={{ display: 'none' }} />
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => ocrFileRef.current?.click()}
                disabled={ocrLoading}
                data-testid="reserv-ocr-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#F3E8FF', borderColor: '#7B1FA2', color: '#7B1FA2' }}
                title="Importer une liste manuscrite via IA Gemini"
              >
                <Sparkle size={14} weight="bold" /> {ocrLoading ? 'Analyse IA...' : 'Importer via photo (IA)'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleExportPDF} data-testid="reserv-export-pdf" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <FilePdf size={14} weight="bold" /> PV de levee (PDF)
              </button>
              {data.all_signed && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSendPV}
                  disabled={busy}
                  data-testid="reserv-send-pv"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#2E7D32', borderColor: '#2E7D32' }}
                  title={data.pv_sent_at ? `Deja envoye le ${fmtDateTimeFR(data.pv_sent_at)} - cliquer pour renvoyer` : 'Envoyer le PV au client par email'}
                >
                  <PaperPlaneRight size={14} weight="bold" /> {data.pv_sent_at ? 'Renvoyer le PV' : 'Envoyer le PV au client'}
                </button>
              )}
            </div>
          )}
        </div>
        <div style={{ height: '8px', background: '#E2E2E6', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ width: `${data.progress_pct}%`, height: '100%', background: 'linear-gradient(90deg, #2E7D32, #66BB6A)', transition: 'width 0.3s' }} />
        </div>
        {isAdmin && data.pv_sent_at && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: '#E8F5E9', borderLeft: '3px solid #2E7D32', borderRadius: '4px', fontSize: '0.82rem', color: '#1B5E20', display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="pv-sent-banner">
            <EnvelopeSimple size={14} weight="bold" />
            PV envoye le {fmtDateTimeFR(data.pv_sent_at)} a <b>{data.pv_sent_to}</b>
          </div>
        )}
        <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['all', 'en_cours', 'fait', 'pas_fait'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`reserv-filter-${f}`}
              style={{
                padding: '4px 12px', borderRadius: '12px', border: 'none',
                fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                backgroundColor: filter === f ? '#002FA7' : '#F4F5F7',
                color: filter === f ? 'white' : '#5E5F66'
              }}>
              {f === 'all' ? `Toutes (${data.total})` : STATUS_META[f]?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filter buttons + Add button */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && (
            <select
              className="form-input"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              data-testid="reserv-filter-assignee"
              style={{ width: 240, fontSize: '0.82rem' }}
            >
              <option value="all">Tous les affectes</option>
              <option value="none">Non affecte</option>
              <optgroup label="Sous-traitants">
                {subcontractors.map((s) => (
                  <option key={`s-${s.id}`} value={s.id}>ST: {s.company_name || s.name}</option>
                ))}
              </optgroup>
              <optgroup label="Salaries">
                {employees.map((u) => (
                  <option key={`e-${u.id}`} value={u.id}>{u.name}</option>
                ))}
              </optgroup>
            </select>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClientLink}
              disabled={clientLinkLoading}
              data-testid="reserv-client-link-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Generer un lien public pour que le client suive les reserves en lecture seule"
            >
              <Link size={16} weight="bold" /> {clientLinkLoading ? 'Generation...' : 'Lien client'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
            data-testid="reserv-add-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={16} weight="bold" /> Nouvelle reserve
          </button>
        </div>
      </div>

      {showAddModal && (
        <AddReservationModal
          project={project}
          isAdmin={isAdmin}
          subcontractors={subcontractors}
          employees={employees}
          knownLocations={knownLocations}
          onClose={() => setShowAddModal(false)}
          onCreated={() => { fetchData(); fetchLocations(); }}
        />
      )}

      {/* Table */}
      {filteredItems.length === 0 ? (
        <div className="card" style={{ padding: '30px', textAlign: 'center', color: '#5E5F66' }}>Aucune reserve</div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: '0.88rem' }}>
            <thead>
              <tr>
                <th>Reserve</th>
                <th>Etat</th>
                <th>Ouverture</th>
                <th>Levee</th>
                <th>Photos</th>
                <th>Affecte a</th>
                <th>Signature</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(r => (
                <ReservationRow
                  key={r.id} r={r} isAdmin={isAdmin}
                  onStatusChange={handleStatusChange} onDelete={handleDelete}
                  onEdit={setEditTarget} onUploadPhoto={handleUploadPhoto}
                  onSign={setSignTarget} onReport={setReportTarget}
                  onViewIssues={setIssuesTarget} onAssignST={setStTarget}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {signTarget && (
        <SignatureModal
          title="Signature client - levee de reserve"
          reservationTitle={signTarget.title}
          onClose={() => setSignTarget(null)}
          onSave={handleSign}
          saving={busy}
        />
      )}
      {reportTarget && (<ReportIssueDialog reservation={reportTarget} onClose={() => setReportTarget(null)} onReport={handleReport} />)}
      {editTarget && (<EditDialog reservation={editTarget} onClose={() => setEditTarget(null)} onSave={handleEdit} />)}
      {issuesTarget && (<IssuesDialog reservation={issuesTarget} onClose={() => setIssuesTarget(null)} />)}
      {stTarget && (<AssignSTDialog reservation={stTarget} project={project} onClose={() => setStTarget(null)} onRefresh={fetchData} />)}
      {ocrResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} data-testid="ocr-preview-modal">
          <div style={{ background: 'white', borderRadius: '10px', maxWidth: '720px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #7B1FA2, #AB47BC)', color: 'white' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkle size={18} weight="bold" /> Reserves detectees par l'IA
              </h3>
              <button onClick={() => setOcrResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}><X size={20} /></button>
            </div>
            <div style={{ padding: '14px 18px', background: '#F8F9FB', fontSize: '0.85rem', color: '#5E5F66' }}>
              Cochez les lignes a importer. Vous pouvez modifier chaque champ avant validation.
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px' }}>
              {ocrResult.reservations && ocrResult.reservations.length > 0 ? (
                ocrResult.reservations.map((r, idx) => (
                  <div key={idx} style={{ padding: '10px 12px', border: '1px solid #E2E2E6', borderLeft: `3px solid ${ocrSelection[idx] ? '#7B1FA2' : '#C2C3C9'}`, borderRadius: '6px', marginBottom: '8px', display: 'flex', gap: '10px', alignItems: 'flex-start', background: ocrSelection[idx] ? '#FAF5FF' : 'white' }}>
                    <input type="checkbox" checked={!!ocrSelection[idx]} onChange={() => setOcrSelection(s => ({ ...s, [idx]: !s[idx] }))} data-testid={`ocr-cb-${idx}`} style={{ width: '18px', height: '18px', marginTop: '8px', accentColor: '#7B1FA2' }} />
                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 150px', gap: '6px' }}>
                      <input type="text" className="form-input" value={r.title || ''} onChange={(e) => setOcrResult(o => ({ ...o, reservations: o.reservations.map((x, i) => i === idx ? { ...x, title: e.target.value } : x) }))} data-testid={`ocr-title-${idx}`} placeholder="Titre" />
                      <input type="text" className="form-input" value={r.location || ''} onChange={(e) => setOcrResult(o => ({ ...o, reservations: o.reservations.map((x, i) => i === idx ? { ...x, location: e.target.value } : x) }))} data-testid={`ocr-location-${idx}`} placeholder="Lieu" />
                      {r.description && <input type="text" className="form-input" value={r.description || ''} onChange={(e) => setOcrResult(o => ({ ...o, reservations: o.reservations.map((x, i) => i === idx ? { ...x, description: e.target.value } : x) }))} style={{ gridColumn: '1 / 3' }} placeholder="Description" />}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '30px', textAlign: 'center', color: '#5E5F66' }}>
                  {ocrResult.error || 'Aucune reserve detectee dans ce document.'}
                </div>
              )}
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <div style={{ fontSize: '0.85rem', color: '#5E5F66' }}>
                <b style={{ color: '#7B1FA2' }}>{Object.values(ocrSelection).filter(Boolean).length}</b> / {ocrResult.reservations?.length || 0} selectionnee(s)
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-secondary" onClick={() => setOcrResult(null)} disabled={busy} data-testid="ocr-cancel">Annuler</button>
                <button className="btn btn-primary" onClick={confirmOcrImport} disabled={busy || Object.values(ocrSelection).filter(Boolean).length === 0} data-testid="ocr-confirm" style={{ background: '#7B1FA2', borderColor: '#7B1FA2' }}>
                  {busy ? 'Import...' : 'Importer la selection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Assign Reservation to ST or Salarie + (optional) letter via Gemini (ST only)
// ============================================================================
const AssignSTDialog = ({ reservation, project, onClose, onRefresh }) => {
  const [step, setStep] = useState(1); // 1=assign 2=letter (ST only)
  const [assigneeType, setAssigneeType] = useState(
    reservation.assigned_to_type || (reservation.subcontractor_id ? 'subcontractor' : 'subcontractor')
  );
  const [subcontractors, setSubcontractors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assigneeId, setAssigneeId] = useState(reservation.assigned_to_id || reservation.subcontractor_id || '');
  const [busy, setBusy] = useState(false);
  const [letterKind, setLetterKind] = useState('initial');
  const [letterText, setLetterText] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/subcontractors`, { withCredentials: true })
      .then(r => setSubcontractors(r.data || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/users/employees`, { withCredentials: true })
      .then(r => setEmployees(r.data || []))
      .catch(() => {});
  }, []);

  const handleAssign = async () => {
    if (!assigneeId) return toast.error('Selectionner une personne');
    setBusy(true);
    try {
      if (assigneeType === 'subcontractor') {
        // Use legacy ST endpoint (also writes a letter step path) for ST flow continuity
        await axios.post(`${API_URL}/api/subcontractors/reservations/${reservation.id}/assign`, { subcontractor_id: assigneeId }, { withCredentials: true });
        toast.success('Sous-traitant affecte');
        onRefresh && onRefresh();
        setStep(2);  // continue to letter generation
      } else {
        // Unified endpoint for employees (no letter step)
        await axios.post(`${API_URL}/api/reservations/${reservation.id}/assign`, { assignee_id: assigneeId, assignee_type: 'employee' }, { withCredentials: true });
        toast.success('Salarie affecte');
        onRefresh && onRefresh();
        onClose();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API_URL}/api/subcontractors/reservations/${reservation.id}/generate-letter`, { kind: letterKind }, { withCredentials: true });
      setLetterText(r.data.draft || '');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur Gemini'); }
    finally { setGenerating(false); }
  };

  const handleSend = async () => {
    if (!letterText.trim()) return toast.error('Texte vide');
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/subcontractors/reservations/${reservation.id}/send-letter`, { body_text: letterText, kind: letterKind }, { withCredentials: true });
      toast.success('Courrier envoyé par email');
      onRefresh && onRefresh();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const handleDownloadPdf = async () => {
    if (!letterText.trim()) return toast.error('Aucun texte');
    try {
      await axios.post(`${API_URL}/api/subcontractors/reservations/${reservation.id}/send-letter`, { body_text: letterText, kind: letterKind }, { withCredentials: true }).catch(() => {});
    } catch (e) { /* noop */ }
    try {
      const r = await axios.get(`${API_URL}/api/subcontractors/reservations/${reservation.id}/letter-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `courrier_reserve_${reservation.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur PDF'); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10005, padding: 20 }} data-testid="reserv-st-modal">
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Réserve « {reservation.title} » — Affectation</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {step === 1 && (
            <>
              <p style={{ marginTop: 0, color: '#5E5F66' }}>Selectionnez la personne en charge de la levee de cette reserve.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10 }}>
                <select className="form-input" value={assigneeType} onChange={(e) => { setAssigneeType(e.target.value); setAssigneeId(''); }} data-testid="reserv-st-type">
                  <option value="subcontractor">Sous-traitant</option>
                  <option value="employee">Salarie</option>
                </select>
                <select className="form-input" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} data-testid="reserv-st-select">
                  <option value="">-- Selectionner --</option>
                  {assigneeType === 'subcontractor' && subcontractors.map(s => <option key={s.id} value={s.id}>{s.company_name || s.name}{s.email ? ` (${s.email})` : ''}</option>)}
                  {assigneeType === 'employee' && employees.map(u => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` - ${u.job_title}` : ''}</option>)}
                </select>
              </div>
              {assigneeType === 'employee' && (
                <p style={{ marginTop: 10, fontSize: '0.82rem', color: '#5E5F66', background: '#F0FDF4', padding: '8px 12px', borderRadius: 6, borderLeft: '3px solid #16A34A' }}>
                  Aucun courrier de notification n'est envoye automatiquement pour un salarie interne.
                </p>
              )}
            </>
          )}
          {step === 2 && (
            <>
              <div style={{ marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ fontWeight: 600 }}>Type de courrier :</label>
                <label><input type="radio" name="kind" checked={letterKind === 'initial'} onChange={() => setLetterKind('initial')} /> Notification initiale</label>
                <label><input type="radio" name="kind" checked={letterKind === 'reminder'} onChange={() => setLetterKind('reminder')} /> Relance + pénalités (15j)</label>
                <button className="btn btn-secondary btn-sm" onClick={handleGenerate} disabled={generating} data-testid="reserv-generate-letter"><Sparkle size={14} /> {generating ? 'Génération…' : 'Générer avec IA'}</button>
              </div>
              <textarea className="form-input" rows={18} value={letterText} onChange={(e) => setLetterText(e.target.value)} placeholder="Le courrier généré apparaîtra ici. Vous pouvez l'éditer avant envoi." data-testid="reserv-letter-text" style={{ fontFamily: 'serif', fontSize: '0.92rem', lineHeight: 1.6 }} />
            </>
          )}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step === 1 && <button className="btn btn-primary" onClick={handleAssign} disabled={busy} data-testid="reserv-st-assign">Affecter →</button>}
            {step === 2 && (
              <>
                <button className="btn btn-secondary" onClick={handleDownloadPdf} disabled={!letterText} data-testid="reserv-letter-pdf"><FilePdf size={14} /> PDF officiel</button>
                <button className="btn btn-primary" onClick={handleSend} disabled={busy || !letterText} data-testid="reserv-letter-send"><PaperPlaneRight size={14} /> Envoyer par email</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
